import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme, alpha } from "@material-ui/core/styles";
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Collapse,
  List,
  Tooltip,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  HelpCircle as HelpOutline,
  ChevronUp as ExpandLess,
  ChevronDown as ExpandMore,
  Smartphone as PhonelinkSetupRounded,
  // Ícones do menu lateral — todos lucide-react (24px default)
  Zap as QuickReplyIcon,
  ChartLine as GerenciaIcon,
  TrendingUp as DashboardIcon,
  FileBarChart as RelatoriosIcon,
  LayoutGrid as PainelIcon,
  Kanban as KanbanIcon,
  Contact as ContatosIcon,
  Stethoscope as ClinicIcon,
  CalendarDays as AgendamentosIcon,
  Tag as TagIcon,
  Megaphone as CampanhasIcon,
  Database as ListagemIcon,
  FileText as DocIcon,
  Database as StorageIcon,
  Users as ListaContatosIcon,
  Settings as ConfiguracoesIcon,
  Workflow as FluxoIcon,
  GitBranch as FluxoCampanhaIcon,
  Waypoints as FluxoConversaIcon,
  Bell as InformativosIcon,
  Code2 as ApiIcon,
  User as UsuariosIcon,
  Inbox as FilasIcon,
  Sparkles as PromptsIcon,
  Plug as IntegracoesIcon,
  Wifi as ConexoesIcon,
  BadgeCheck as AssinantesIcon,
  Handshake as AfiliadosIcon,
  CreditCard as PagamentosIcon,
  Wallet as FinanceiroIcon,
  MessagesSquare as AtendimentosIcon,
  Building2 as EmpresasIcon,
} from "lucide-react";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";
import { Can } from "../components/Can";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import { i18n } from "../translate/i18n";
import useHelps from "../hooks/useHelps";


// ---------------- Icon palette ----------------
const iconStyles = {
  dashboard: { color: "#6366f1", gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)" },
  tickets: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  messages: { color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)" },
  kanban: { color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)" },
  contacts: { color: "#06b6d4", gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" },
  schedules: { color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)" },
  clinic: { color: "#e11d48", gradient: "linear-gradient(135deg, #e11d48 0%, #be123c 100%)" },
  tags: { color: "#14b8a6", gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" },
  chats: { color: "#f97316", gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" },
  helps: { color: "#3b82f6", gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" },
  campaigns: { color: "#ef4444", gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
  flowbuilder: { color: "#84cc16", gradient: "linear-gradient(135deg, #84cc16 0%, #65a30d 100%)" },
  knowledgeBase: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  announcements: { color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
  api: { color: "#06b6d4", gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" },
  users: { color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
  queues: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #047857 100%)" },
  prompts: { color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
  integrations: { color: "#f97316", gradient: "linear-gradient(135deg, #f97316 0%, #c2410c 100%)" },
  connections: { color: "#64748b", gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)" },
  files: { color: "#14b8a6", gradient: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)" },
  financial: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #065f46 100%)" },
  settings: { color: "#6366f1", gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" },
  companies: { color: "#3b82f6", gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" },
  subscribers: { color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
  affiliates: { color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
  paymentConfig: { color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
};

// ---------------- i18n helpers ----------------
const lang = () => (i18n?.language || "pt").split("-")[0].toLowerCase();

/**
 * tLang: tenta i18n.t(key). Se não existir, usa fallback por idioma atual.
 * Ex.: tLang("mainDrawer.listItems.tickets", "Atendimentos", "Services")
 */
const tLang = (key, pt, en) => {
  try {
    const v = i18n.t(key);
    if (v && v !== key) return v;
  } catch (_) {}
  return lang() === "en" ? en : pt;
};

// ---------------- theme helpers ----------------
const getPaletteMode = (theme) => theme?.mode ?? theme?.palette?.mode ?? theme?.palette?.type ?? "light";
const labelColor = (theme, isActive, iconColor) =>
  getPaletteMode(theme) === "dark" ? "#ffffff" : (isActive ? iconColor : "#666");

// ---------------- reducer ----------------
const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];
    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) state[chatIndex] = chat;
        else newChats.push(chat);
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);
    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    }
    return [chat, ...state];
  }
  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) state.splice(chatIndex, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
  if (action.type === "CHANGE_CHAT") {
    return state.map((chat) => (chat.id === action.payload.chat.id ? action.payload.chat : chat));
  }
  return state;
};

// ---------------- Item de link ----------------
function ListItemLink({ icon, primary, to, showBadge, iconKey, small, collapsed }) {
  const theme = useTheme();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;
  const iconStyle = iconStyles[iconKey] || iconStyles.dashboard;
  const gliderColor = iconStyle.color;
  const sidebarTextColor = theme.chatiaDS?.sidebar?.textColor || "#ffffff";
  const sidebarMutedTextColor = theme.chatiaDS?.sidebar?.mutedTextColor || "rgba(255,255,255,0.7)";

  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to]
  );

  const textColor = sidebarMutedTextColor;
  const activeTextColor = sidebarTextColor;

  return (
    <Tooltip title={collapsed ? primary : ""} placement="right">
      <li style={{ position: "relative" }}>
        <ListItem
          button
          component={renderLink}
          sx={{
            borderRadius: "12px",
            mx: collapsed ? "auto" : 1,
            my: 0.25,
            width: collapsed ? 44 : "calc(100% - 16px)",
            boxSizing: "border-box",
            minHeight: small ? 38 : 42,
            pl: collapsed ? 0 : small ? 3 : 1.5,
            pr: collapsed ? 0 : 2,
            justifyContent: collapsed ? "center" : "flex-start",
            position: "relative",
            overflow: "hidden",
            transition: "all 200ms ease",
            background: isActive ? "rgba(255,255,255,0.10)" : "transparent",
            border: isActive
              ? "1px solid rgba(255,255,255,0.10)"
              : "1px solid transparent",
            "&:hover": {
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.10)",
            },
          }}
        >
          {icon && (
            <ListItemIcon
              sx={{
                minWidth: collapsed ? "auto" : 36,
                justifyContent: "center",
              }}
            >
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "10px",
                      height: 16,
                      minWidth: 16,
                      padding: "0 4px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: isActive ? activeTextColor : textColor,
                      transition: "color 300ms ease",
                      "& svg": { color: "inherit !important" },
                    }}
                  >
                    {icon}
                  </Box>
                </Badge>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isActive ? activeTextColor : textColor,
                    transition: "color 300ms ease",
                    "& svg": { color: "inherit !important" },
                  }}
                >
                  {icon}
                </Box>
              )}
            </ListItemIcon>
          )}
          {!collapsed && (
            <ListItemText
              primary={primary}
              primaryTypographyProps={{
                sx: {
                  fontSize: "0.85rem",
                  fontWeight: isActive ? 500 : 400,
                  color: isActive ? activeTextColor : textColor,
                  transition: "all 300ms ease",
                },
              }}
            />
          )}
        </ListItem>
      </li>
    </Tooltip>
  );
}

// ---------------- Main ----------------
const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openKnowledgeSubmenu, setOpenKnowledgeSubmenu] = useState(false);
  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [openDashboardSubmenu, setOpenDashboardSubmenu] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);

  // re-render no changeLanguage
  const [, force] = useState(0);
  useEffect(() => {
    const rerender = () => force((n) => n + 1);
    i18n.on("languageChanged", rerender);
    return () => i18n.off("languageChanged", rerender);
  }, []);

  useEffect(() => {
    async function checkHelps() {
      // Não fazer requisição se não houver usuário autenticado
      if (!user || !user.id) {
        setHasHelps(false);
        return;
      }

      try {
        const helps = await list();
        setHasHelps(helps.length > 0);
      } catch (error) {
        // Ignora erros de autenticação durante logout
        if (error?.response?.status !== 401) {
          console.error("Erro ao buscar helps:", error);
        }
        setHasHelps(false);
      }
    }
    checkHelps();
  }, [list, user]);

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists");

  const isKnowledgeRouteActive =
    location.pathname.startsWith("/knowledge-base");

  const isFlowbuilderRouteActive =
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilders");

  useEffect(() => {
    setActiveMenu(location.pathname.startsWith("/tickets") ? "/tickets" : "");
  }, [location, setActiveMenu]);

  useEffect(() => {
    if (collapsed) {
      setOpenCampaignSubmenu(false);
      setOpenFlowSubmenu(false);
      setOpenDashboardSubmenu(false);
    }
  }, [collapsed]);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      if (user.companyId) {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        // Verificar se plan existe antes de acessar propriedades
        if (planConfigs && planConfigs.plan) {
          setShowCampaigns(planConfigs.plan.useCampaigns);
          setShowKanban(planConfigs.plan.useKanban);
          setShowOpenAi(planConfigs.plan.useOpenAi);
          setShowIntegrations(planConfigs.plan.useIntegrations);
          setShowSchedules(planConfigs.plan.useSchedules);
          setShowInternalChat(false);
          setShowExternalApi(planConfigs.plan.useExternalApi);
        }
      }
    }
    fetchData();
  }, [user, getPlanCompany]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (showInternalChat) fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, showInternalChat]);

  useEffect(() => {
    if (!showInternalChat) return undefined;
    if (user.id) {
      const companyId = user.companyId;
      const onCompanyChat = (data) => {
        if (["new-message", "update"].includes(data.action)) {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };
      socket.on(`company-${companyId}-chat`, onCompanyChat);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChat);
      };
    }
  }, [socket, user.id, user.companyId, showInternalChat]);

  useEffect(() => {
    const t = setTimeout(() => {
      const offline = whatsApps.some((w) =>
        ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
      );
      setConnectionWarning(offline);
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const sidebarTextColor = theme.chatiaDS?.sidebar?.textColor || "#ffffff";
  const sidebarMutedTextColor = theme.chatiaDS?.sidebar?.mutedTextColor || "rgba(255,255,255,0.7)";

  const SectionHeader = ({ children }) =>
    !collapsed && (
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.7rem",
          textTransform: "uppercase",
          padding: "20px 16px 6px",
          lineHeight: 1,
          letterSpacing: "0.8px",
          color: alpha(sidebarTextColor, 0.45),
        }}
      >
        {children}
      </Typography>
    );

  const SubmenuItem = (props) => <ListItemLink small {...props} />;

  // ExpandableMenuItem agora se comporta como link único (sem accordion).
  // O usuário pediu "deixar fixo": cada agrupamento aponta direto pra uma página
  // hub que vai mostrar as 3 abas internas (Listagem | Contatos | Config etc).
  // Por enquanto resolvemos pra primeira sub-rota — e o `children` vira opcional.
  const ExpandableMenuItem = ({
    icon,
    primary,
    iconKey,
    isActive,
    isOpen,
    onToggle,
    children,
    to,
  }) => {
    const iconStyle = iconStyles[iconKey] || iconStyles.dashboard;
    const gliderColor = iconStyle.color;

    // Resolve uma rota padrão a partir do primeiro <SubmenuItem to="..."> filho.
    const childArr = React.Children.toArray(children).filter(Boolean);
    const fallbackTo =
      to ||
      (childArr[0] && childArr[0].props && childArr[0].props.to) ||
      "#";

    return (
      <ListItemLink
        to={fallbackTo}
        primary={primary}
        icon={icon}
        iconKey={iconKey}
        collapsed={collapsed}
      />
    );
  };

  // Versão antiga (com accordion) — desligada, mantida pra referência.
  // eslint-disable-next-line no-unused-vars
  const _ExpandableMenuItemAccordion = ({ icon, primary, iconKey, isActive, isOpen, onToggle, children }) => {
    const iconStyle = iconStyles[iconKey] || iconStyles.dashboard;
    const gliderColor = iconStyle.color;

    if (collapsed) {
      return (
        <ListItemLink
          to="#"
          primary={primary}
          icon={icon}
          iconKey={iconKey}
          collapsed={collapsed}
        />
      );
    }

    return (
      <>
        <ListItem
          button
          onClick={onToggle}
          sx={{
            borderRadius: 0,
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            ml: 1,
            mr: 0,
            my: 0.25,
            minHeight: 44,
            pl: 1.5,
            transition: "all 300ms ease-in-out",
            background: "transparent",
            "&:hover": { background: alpha(sidebarTextColor, 0.08) },
          }}
        >
          <ListItemIcon sx={{
            minWidth: 36,
            color: isActive ? sidebarTextColor : sidebarMutedTextColor,
            transition: "color 300ms ease",
            "& svg": { color: "inherit !important" },
          }}>{icon}</ListItemIcon>
          <ListItemText
            primary={primary}
            primaryTypographyProps={{
              sx: {
                fontSize: "0.85rem",
                fontWeight: isActive ? 500 : 400,
                color: isActive ? sidebarTextColor : sidebarMutedTextColor,
                transition: "all 300ms ease",
              },
            }}
          />
          {isOpen ? (
            <ExpandLess sx={{ color: alpha(sidebarTextColor, 0.55), fontSize: 20 }} />
          ) : (
            <ExpandMore sx={{ color: alpha(sidebarTextColor, 0.55), fontSize: 20 }} />
          )}
        </ListItem>
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
          sx={{
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            mx: 1,
            borderRadius: 2,
          }}
        >
          <List dense component="div" disablePadding>
            {children}
          </List>
        </Collapse>
      </>
    );
  };

  // ---------------- Labels com fallback por idioma ----------------
  const L = {
    // Gerência
    management: tLang("mainDrawer.listItems.management", "Análise", "Analytics"),
    dashboard: tLang("mainDrawer.listItems.dashboard", "Dashboard", "Dashboard"),
    reports: tLang("mainDrawer.listItems.reports", "Relatórios", "Reports"),
    realtime: tLang("mainDrawer.listItems.chatsTempoReal", "Painel", "Panel"),

    // Principais
    tickets: tLang("mainDrawer.listItems.tickets", "Atendimentos", "Services"),
    quick: tLang("mainDrawer.listItems.quickMessages", "Respostas rápidas", "Quick Responses"),
    kanban: tLang("mainDrawer.listItems.kanban", "Kanban", "Kanban"),
    contacts: tLang("mainDrawer.listItems.contacts", "Contatos", "Contacts"),
    schedules: tLang("mainDrawer.listItems.schedules", "Agendamentos", "Schedules"),
    clinic: tLang("clinic.title", "Clínica", "Clinic"),
    tags: tLang("mainDrawer.listItems.tags", "Tags", "Tags"),
    internalChat: tLang("mainDrawer.listItems.internalChat", "Chat Interno", "Internal Chat"),
    helps: tLang("mainDrawer.listItems.helps", "Ajuda", "Help"),

    // Administração
    admin: tLang("mainDrawer.listItems.administration", "Administração", "Administration"),
    campaigns: tLang("mainDrawer.listItems.campaigns", "Campanhas", "Campaigns"),
    campaigns_list: tLang("campaigns.subMenus.list", "Listagem", "List"),
    campaigns_contacts: tLang("campaigns.subMenus.listContacts", "Lista de contatos", "Contact List"),
    campaigns_settings: tLang("campaigns.subMenus.settings", "Configurações", "Settings"),

    flowbuilder: tLang("flowbuilder.title", "Flowbuilder", "Flowbuilder"),
    flowbuilder_campaign: tLang("flowbuilder.subMenus.campaign", "Campaign Flow", "Campaign Flow"),
    flowbuilder_conversation: tLang("flowbuilder.subMenus.conversation", "Conversation Flow", "Conversation Flow"),
    knowledgeBase: tLang("mainDrawer.listItems.knowledgeBase", "Base de Conhecimento", "Knowledge Base"),
    agentQualification: tLang("mainDrawer.listItems.agentQualification", "Qualificação do Agente", "Agent Qualification"),

    announcements: tLang("mainDrawer.listItems.announcements", "Informativos", "Announcements"),
    api: tLang("mainDrawer.listItems.messagesAPI", "API", "API"),
    users: tLang("mainDrawer.listItems.users", "Usuários", "Users"),
    queues: tLang("mainDrawer.listItems.queues", "Filas", "Queues"),
    prompts: tLang("mainDrawer.listItems.prompts", "Prompts", "Prompts"),
    integrations: tLang("mainDrawer.listItems.queueIntegration", "Integrações", "Integrations"),
    connections: tLang("mainDrawer.listItems.connections", "Conexões", "Connections"),
    allConnections: tLang("mainDrawer.listItems.allConnections", "Gerenciar conexões", "Manage connections"),
    files: tLang("mainDrawer.listItems.files", "Arquivos", "File List"),
    financial: tLang("mainDrawer.listItems.financeiro", "Financeiro", "Financial"),
    settings: tLang("mainDrawer.listItems.settings", "Configurações", "Settings"),
    companies: tLang("mainDrawer.listItems.companies", "Empresas", "Companies"),
    subscribers: tLang("subscribers.title", "Assinantes", "Subscribers"),
    affiliates: tLang("affiliates.title", "Afiliados", "Affiliates"),
    paymentConfig: tLang("paymentConfig.title", "Config. Pagamentos", "Payment Config"),
  };

  return (
    <div onClick={drawerClose}>
      <Can
        role={
          (user.profile === "user" &&
            (user.showDashboard === "enabled" || user.allowRealTime === "enabled"))
            ? "admin"
            : user.profile
        }
        perform={"drawer-admin-items:view"}
        yes={() => (
          <ExpandableMenuItem
            to="/"
            icon={<GerenciaIcon size={18} strokeWidth={1.5} />}
            primary={L.management}
            iconKey="dashboard"
            isActive={isManagementActive}
            isOpen={openDashboardSubmenu}
            onToggle={() => setOpenDashboardSubmenu((p) => !p)}
          >
            <Can
              role={
                user.profile === "user" && user.showDashboard === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <>
                  <SubmenuItem
                    to="/"
                    primary={L.dashboard}
                    icon={<DashboardIcon size={18} strokeWidth={1.5} />}
                    iconKey="dashboard"
                  />
                  <SubmenuItem
                    to="/reports"
                    primary={L.reports}
                    icon={<RelatoriosIcon size={18} strokeWidth={1.5} />}
                    iconKey="dashboard"
                  />
                </>
              )}
            />
            <Can
              role={
                user.profile === "user" && user.allowRealTime === "enabled"
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <SubmenuItem
                  to="/moments"
                  primary={L.realtime}
                  icon={<PainelIcon size={18} strokeWidth={1.5} />}
                  iconKey="dashboard"
                />
              )}
            />
          </ExpandableMenuItem>
        )}
      />

      <ListItemLink
        to="/tickets"
        primary={L.tickets}
        icon={<AtendimentosIcon size={18} strokeWidth={1.5} />}
        iconKey="tickets"
        collapsed={collapsed}
      />

      <ListItemLink
        to="/quick-messages"
        primary={L.quick}
        icon={<QuickReplyIcon size={18} strokeWidth={1.5} />}
        iconKey="messages"
        collapsed={collapsed}
      />

      {showKanban && (
        <ListItemLink
          to="/kanban"
          primary={L.kanban}
          icon={<KanbanIcon size={18} strokeWidth={1.5} />}
          iconKey="kanban"
          collapsed={collapsed}
        />
      )}

      <ListItemLink
        to="/contacts"
        primary={L.contacts}
        icon={<ContatosIcon size={18} strokeWidth={1.5} />}
        iconKey="contacts"
        collapsed={collapsed}
      />

      {showSchedules && (
        <ListItemLink
          to="/schedules"
          primary={L.schedules}
          icon={<AgendamentosIcon size={18} strokeWidth={1.5} />}
          iconKey="schedules"
          collapsed={collapsed}
        />
      )}

      {/* Clínica oculta nesta versão */}

      <ListItemLink
        to="/tags"
        primary={L.tags}
        icon={<TagIcon size={18} strokeWidth={1.5} />}
        iconKey="tags"
        collapsed={collapsed}
      />

      {/* Chat interno oculto nesta versão */}

      {hasHelps && (
        <ListItemLink
          to="/helps"
          primary={L.helps}
          icon={<HelpOutline size={18} strokeWidth={1.5} />}
          iconKey="helps"
          collapsed={collapsed}
        />
      )}

      <Can
        role={
          user.profile === "user" && user.allowConnections === "enabled"
            ? "admin"
            : user.profile
        }
        perform="dashboard:view"
        yes={() => (
          <>
            <Divider sx={{ mx: 2, my: 2, backgroundColor: alpha(sidebarTextColor, 0.16) }} />
            <SectionHeader>{L.admin}</SectionHeader>

            {showCampaigns && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ExpandableMenuItem
                    to="/campaigns"
                    icon={<CampanhasIcon size={18} strokeWidth={1.5} />}
                    primary={L.campaigns}
                    iconKey="campaigns"
                    isActive={isCampaignRouteActive}
                    isOpen={openCampaignSubmenu}
                    onToggle={() => setOpenCampaignSubmenu((p) => !p)}
                  >
                    <SubmenuItem
                      to="/campaigns"
                      primary={L.campaigns_list}
                      icon={<ListagemIcon size={18} strokeWidth={1.5} />}
                      iconKey="campaigns"
                    />
                    <SubmenuItem
                      to="/contact-lists"
                      primary={L.campaigns_contacts}
                      icon={<ListaContatosIcon size={18} strokeWidth={1.5} />}
                      iconKey="campaigns"
                    />
                  </ExpandableMenuItem>
                )}
              />
            )}

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ExpandableMenuItem
                  to="/flowbuilders"
                  icon={<FluxoIcon size={18} strokeWidth={1.5} />}
                  primary={L.flowbuilder}
                  iconKey="flowbuilder"
                  isActive={isFlowbuilderRouteActive}
                  isOpen={openFlowSubmenu}
                  onToggle={() => setOpenFlowSubmenu((p) => !p)}
                >
                  <SubmenuItem
                    to="/phrase-lists"
                    primary={L.flowbuilder_campaign}
                    icon={<FluxoCampanhaIcon size={18} strokeWidth={1.5} />}
                    iconKey="flowbuilder"
                  />
                  <SubmenuItem
                    to="/flowbuilders"
                    primary={L.flowbuilder_conversation}
                    icon={<FluxoConversaIcon size={18} strokeWidth={1.5} />}
                    iconKey="flowbuilder"
                  />
                </ExpandableMenuItem>
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ExpandableMenuItem
                  to="/knowledge-base"
                  icon={<ListagemIcon size={18} strokeWidth={1.5} />}
                  primary={L.knowledgeBase}
                  iconKey="knowledgeBase"
                  isActive={isKnowledgeRouteActive}
                  isOpen={openKnowledgeSubmenu}
                  onToggle={() => setOpenKnowledgeSubmenu((p) => !p)}
                >
                  <SubmenuItem
                    to="/knowledge-base"
                    primary={L.knowledgeBase}
                    icon={<DocIcon size={18} strokeWidth={1.5} />}
                    iconKey="knowledgeBase"
                  />
                  <SubmenuItem
                    to="/knowledge-base-qualify"
                    primary={L.agentQualification}
                    icon={<StorageIcon size={18} strokeWidth={1.5} />}
                    iconKey="knowledgeBase"
                  />
                </ExpandableMenuItem>
              )}
            />

            {user.super && (
              <ListItemLink
                to="/announcements"
                primary={L.announcements}
                icon={<InformativosIcon size={18} strokeWidth={1.5} />}
                iconKey="announcements"
                collapsed={collapsed}
              />
            )}

            {showExternalApi && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/messages-api"
                    primary={L.api}
                    icon={<ApiIcon size={18} strokeWidth={1.5} />}
                    iconKey="api"
                    collapsed={collapsed}
                  />
                )}
              />
            )}

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/users"
                  primary={L.users}
                  icon={<UsuariosIcon size={18} strokeWidth={1.5} />}
                  iconKey="users"
                  collapsed={collapsed}
                />
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/queues"
                  primary={L.queues}
                  icon={<FilasIcon size={18} strokeWidth={1.5} />}
                  iconKey="queues"
                  collapsed={collapsed}
                />
              )}
            />

            {/* Prompts oculto nesta versão */}

            {showIntegrations && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/queue-integration"
                    primary={L.integrations}
                    icon={<IntegracoesIcon size={18} strokeWidth={1.5} />}
                    iconKey="integrations"
                    collapsed={collapsed}
                  />
                )}
              />
            )}

            <ListItemLink
              to="/connections"
              primary={L.connections}
              icon={<ConexoesIcon size={18} strokeWidth={1.5} />}
              iconKey="connections"
              collapsed={collapsed}
            />

            {/* REMOVIDO: Lista de arquivos não é mais usado
            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/files"
                  primary={L.files}
                  icon={<AttachFileRounded />}
                  iconKey="files"
                  collapsed={collapsed}
                />
              )}
            />
            */}

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/financeiro"
                  primary={L.financial}
                  icon={<FinanceiroIcon size={18} strokeWidth={1.5} />}
                  iconKey="financial"
                  collapsed={collapsed}
                />
              )}
            />

            <Can
              role={user.profile}
              perform="dashboard:view"
              yes={() => (
                <ListItemLink
                  to="/settings"
                  primary={L.settings}
                  icon={<ConfiguracoesIcon size={18} strokeWidth={1.5} />}
                  iconKey="settings"
                  collapsed={collapsed}
                />
              )}
            />

            {user.super && (
              <ListItemLink
                to="/companies"
                primary={L.companies}
                icon={<EmpresasIcon size={18} strokeWidth={1.5} />}
                iconKey="companies"
                collapsed={collapsed}
              />
            )}

            {/* Assinantes e Afiliados ocultos nesta versão */}

            {/* Config. Pagamentos oculto nesta versão */}
          </>
        )}
      />

      {!collapsed && (
        <React.Fragment>
          <Divider sx={{ mx: 2, my: 2, backgroundColor: alpha(sidebarTextColor, 0.16) }} />
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Chip
              label="V4.6"
              size="small"
              variant="outlined"
              sx={{
                borderColor: alpha(sidebarTextColor, 0.35),
                color: alpha(sidebarTextColor, 0.7),
                fontWeight: 500,
                fontSize: "0.75rem",
              }}
            />
          </Box>
        </React.Fragment>
      )}
    </div>
  );
};

export default MainListItems;
